import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { ForecastView } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { aggText, ageText, chanceText, coversFuture, decimalsOf, forecastAge, nextHours, periodText, quantityText, rangeText, unitOf, type ForecastAgg, type ForecastRead } from '../data/weatherForecast.ts'
import { NoFutureLine } from './ForecastNoFuture.tsx'
import { Pill } from './components.tsx'
import { ForecastSource } from './ForecastSource.tsx'
import { RowLink } from './RowLink.tsx'
import { useNow } from './useNow.ts'

/** M24-W (docs/M24.md "Screens", Today › Prévision; rules 1, 2, 6): the next 24 hours at a glance for the Superintendent and the direction (forecast.read,
 *  never on the shared workstation: the caller decides, Today.tsx). Rain in mm with its HIGHEST HOURLY chance (never a chance for the period), the lowest and
 *  highest temperature, the mean wind's strongest hour with its Beaufort force and the strongest gust, the thunderstorm's highest hourly chance with its fixed
 *  line ("Information, pas une alerte"), the night's ground minimum with its period; every figure under the `derived` line; the age, and a grey "ancienne"
 *  past the connector's staleness. No colour on a value, no verdict, no window: numbers and where they come from. Read online (the forecast is on no device):
 *  without a network it says so. */
export function ForecastCard({ read }: { read: ForecastRead }) {
  const { t } = useTranslation()
  const now = useNow(60_000)
  const v = read.view
  let body: ReactNode
  if (v === undefined) {
    body = !read.online || read.error === 'OFFLINE' ? <p className="meta" data-testid="fc-offline">{t('forecast.needsNetwork')}</p>
      : read.error ? <p className="meta" data-testid="fc-error">{t('forecast.error')}</p>
      : <p className="meta" data-testid="fc-loading">{t('forecast.loading')}</p>
  } else if (v === null) body = <p className="meta" data-testid="fc-none">{t('forecast.none')}</p>
  else body = <ForecastFigures view={v} now={now} receivedAt={read.receivedAt} />
  return (
    <section className="card flex flex-col gap-2" data-testid="today-forecast" aria-labelledby="today-forecast-title">
      <h2 id="today-forecast-title" className="text-lg font-bold">{t('forecast.card.title')}</h2>
      {body}
      <RowLink to="/forecast" icon="weather" tone="water" testId="fc-open" title={t('forecast.card.open')} />
    </section>
  )
}

function Row({ label, testId, children, after }: { label: string; testId: string; children: ReactNode; after?: ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="text-sm font-semibold">{label}</dt>
      <dd className="text-base" data-testid={testId}>{children}</dd>
      {after}
    </div>
  )
}

function ForecastFigures({ view, now, receivedAt }: { view: ForecastView; now: number; receivedAt: number }) {
  const { t, i18n } = useTranslation()
  const { site } = useClub()
  const lang = i18n.language
  const age = forecastAge(view, now, receivedAt)
  // M24 review: an issue that covers no hour to come says so with its age (never five rows of "pas de valeur")
  if (!coversFuture(view, now)) return <NoFutureLine view={view} ageMs={age.ms} stale={age.stale} testId="fc-past" />
  const n = nextHours(view, { now })
  const q = (a: ForecastAgg, code: Parameters<typeof unitOf>[1]) => aggText(a, unitOf(view, code), decimalsOf(view, code), lang, t)
  const chance = (a: ForecastAgg) => chanceText(a, lang, t)
  const rain = n.rain.kind === 'value' ? `${q(n.rain, 'rain_1h')} · ${chance(n.rainChance)}` : q(n.rain, 'rain_1h')
  const temp = n.tmin.kind === 'value' && n.tmax.kind === 'value'
    ? rangeText(n.tmin.value, n.tmax.value, unitOf(view, 'air_temperature'), decimalsOf(view, 'air_temperature'), lang, t)
    : q(n.tmin, 'air_temperature')
  const wind = [
    n.wind.kind === 'value' ? t('forecast.windMean', { speed: quantityText(n.wind.value, unitOf(view, 'wind_speed'), decimalsOf(view, 'wind_speed'), lang), force: n.force ?? '—' }) : q(n.wind, 'wind_speed'),
    n.gust.kind === 'value' ? t('forecast.gusts', { gust: quantityText(n.gust.value, unitOf(view, 'wind_gust_1h'), decimalsOf(view, 'wind_gust_1h'), lang) }) : null,
  ].filter(Boolean).join(' · ')
  const g = n.groundMin
  const ground = g.kind === 'value' ? `${quantityText(g.value, unitOf(view, 'ground_temperature_min_12h'), decimalsOf(view, 'ground_temperature_min_12h'), lang)} ${periodText(g.from, g.to, site.timezone, t)}`
    : g.kind === 'notProvided' ? t('forecast.notProvided') : t('forecast.noValue')
  return (
    <>
      <dl className="grid gap-2 sm:grid-cols-2">
        <Row label={t('forecast.rain')} testId="fc-rain">{rain}</Row>
        <Row label={t('forecast.temperature')} testId="fc-temp">{temp}</Row>
        <Row label={t('forecast.wind')} testId="fc-wind">{wind}</Row>
        <Row label={t('forecast.thunder')} testId="fc-thunder" after={<p className="meta" data-testid="fc-thunder-line">{t('forecast.thunderLine')}</p>}>{chance(n.thunderChance)}</Row>
        <Row label={t('forecast.groundMin')} testId="fc-ground">{ground}</Row>
      </dl>
      <p className="meta flex flex-wrap items-center gap-2">
        <span data-testid="fc-age">{t('forecast.issuedAgo', { age: ageText(age.ms, t) })}</span>
        {age.stale && <Pill tone="gray"><span data-testid="fc-stale">{t('forecast.stale')}</span></Pill>}
      </p>
      <ForecastSource view={view} mode="derived" />
    </>
  )
}
